import React, { useState, useEffect, useCallback } from 'react';
import { LLM_PROVIDERS, getProviderById } from '../../utils/llmProviders';

const STATUS = {
  UNTESTED: 'untested',
  TESTING: 'testing',
  VALID: 'valid',
  INVALID: 'invalid',
};

const statusConfig = {
  [STATUS.UNTESTED]: { color: '#64748b', bg: 'rgba(100,116,139,0.15)', label: 'Not tested' },
  [STATUS.TESTING]: { color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', label: 'Testing...' },
  [STATUS.VALID]: { color: '#22c55e', bg: 'rgba(34,197,94,0.15)', label: 'Connected' },
  [STATUS.INVALID]: { color: '#ef4444', bg: 'rgba(239,68,68,0.15)', label: 'Failed' },
};

function LLMConfigTab({ profile, onSave, saving, testLLMConnection }) {
  const [selectedProvider, setSelectedProvider] = useState('anthropic');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(STATUS.UNTESTED);
  const [statusMessage, setStatusMessage] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    if (profile) {
      setSelectedProvider(profile.selected_llm_provider || 'anthropic');
      if (profile.llm_api_key_last4) {
        setConnectionStatus(profile.llm_connection_verified ? STATUS.VALID : STATUS.UNTESTED);
      }
    }
  }, [profile]);

  const provider = getProviderById(selectedProvider);

  const getMaskedKey = useCallback(() => {
    if (!profile?.llm_api_key_last4) return '';
    const prov = getProviderById(profile.selected_llm_provider);
    const prefix = prov?.apiKeyPrefix || '';
    return `${prefix}${'*'.repeat(20)}${profile.llm_api_key_last4}`;
  }, [profile]);

  const handleProviderSelect = (id) => {
    setSelectedProvider(id);
    setApiKey('');
    setShowKey(false);
    setConnectionStatus(STATUS.UNTESTED);
    setStatusMessage('');
  };

  const handleTestConnection = async () => {
    if (!apiKey.trim()) {
      setStatusMessage('Please enter an API key first.');
      setConnectionStatus(STATUS.INVALID);
      return;
    }

    setConnectionStatus(STATUS.TESTING);
    setStatusMessage('Verifying your API key...');

    const result = await testLLMConnection(selectedProvider, apiKey);

    if (result.success) {
      setConnectionStatus(STATUS.VALID);
      setStatusMessage(result.message);
    } else {
      setConnectionStatus(STATUS.INVALID);
      setStatusMessage(result.message);
    }
  };

  const handleSave = () => {
    const updates = {
      selected_llm_provider: selectedProvider,
      selected_llm_model: provider?.model || '',
    };

    if (apiKey.trim()) {
      updates.llm_api_key_encrypted = apiKey;
      updates.llm_api_key_last4 = apiKey.slice(-4);
      updates.llm_connection_verified = connectionStatus === STATUS.VALID;
      updates.llm_verified_at = connectionStatus === STATUS.VALID ? new Date().toISOString() : null;
    }

    onSave(updates);
  };

  const cardStyles = {
    card: {
      background: '#0f2040',
      border: '1px solid #1e3a5f',
      borderRadius: '12px',
      padding: '24px',
    },
    providerGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '12px',
      marginBottom: '28px',
    },
    providerCard: (isSelected) => ({
      background: isSelected ? 'rgba(245, 158, 11, 0.06)' : '#0a1628',
      border: isSelected ? '2px solid #f59e0b' : '1px solid #1e3a5f',
      borderRadius: '10px',
      padding: '14px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      position: 'relative',
    }),
    iconCircle: (color) => ({
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: color || '#1e3a5f',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
      fontWeight: '700',
      fontSize: '14px',
      marginBottom: '10px',
      flexShrink: 0,
    }),
    providerName: {
      color: '#f1f5f9',
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '2px',
    },
    providerModel: {
      color: '#64748b',
      fontSize: '11px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    badge: (type) => {
      const colors = {
        Recommended: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' },
        Popular: { bg: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa' },
        Fastest: { bg: 'rgba(34, 197, 94, 0.15)', color: '#22c55e' },
        Budget: { bg: 'rgba(168, 85, 247, 0.15)', color: '#a855f7' },
        Enterprise: { bg: 'rgba(100, 116, 139, 0.15)', color: '#94a3b8' },
        'Open Source': { bg: 'rgba(34, 197, 94, 0.15)', color: '#22c55e' },
        'Multi-Provider': { bg: 'rgba(99, 102, 241, 0.15)', color: '#818cf8' },
      };
      const c = colors[type] || { bg: 'rgba(100, 116, 139, 0.15)', color: '#94a3b8' };
      return {
        display: 'inline-block',
        fontSize: '10px',
        fontWeight: '600',
        padding: '2px 8px',
        borderRadius: '9999px',
        background: c.bg,
        color: c.color,
        marginTop: '6px',
      };
    },
    checkmark: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      background: '#f59e0b',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#0a1628',
      fontSize: '12px',
      fontWeight: '700',
    },
    input: {
      width: '100%',
      padding: '10px 14px',
      background: '#0a1628',
      border: '1px solid #1e3a5f',
      borderRadius: '8px',
      color: '#f1f5f9',
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    inputFocused: {
      width: '100%',
      padding: '10px 14px',
      background: '#0a1628',
      border: '1px solid #f59e0b',
      borderRadius: '8px',
      color: '#f1f5f9',
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#f1f5f9',
      marginBottom: '6px',
    },
    btnPrimary: {
      background: '#f59e0b',
      color: '#0a1628',
      fontWeight: '600',
      padding: '10px 24px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '14px',
      cursor: 'pointer',
    },
    btnSecondary: {
      background: 'transparent',
      border: '1px solid #1e3a5f',
      color: '#f1f5f9',
      fontWeight: '600',
      padding: '10px 24px',
      borderRadius: '8px',
      fontSize: '14px',
      cursor: 'pointer',
    },
  };

  return (
    <div>
      {/* Provider Grid */}
      <div style={cardStyles.card}>
        <h3 style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: '700', marginBottom: '4px', marginTop: '0' }}>
          LLM Provider
        </h3>
        <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '20px', marginTop: '0' }}>
          Select the AI model that powers your tactical analysis engine.
        </p>

        <div style={cardStyles.providerGrid}>
          {LLM_PROVIDERS.map((p) => {
            const isSelected = selectedProvider === p.id;
            return (
              <div
                key={p.id}
                style={cardStyles.providerCard(isSelected)}
                onClick={() => handleProviderSelect(p.id)}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#f59e0b';
                    e.currentTarget.style.background = 'rgba(245, 158, 11, 0.03)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#1e3a5f';
                    e.currentTarget.style.background = '#0a1628';
                  }
                }}
              >
                {isSelected && (
                  <div style={cardStyles.checkmark}>&#10003;</div>
                )}
                <div style={cardStyles.iconCircle(p.color)}>
                  {p.icon}
                </div>
                <div style={cardStyles.providerName}>{p.name}</div>
                <div style={cardStyles.providerModel}>{p.model}</div>
                {p.badge && (
                  <span
                    style={{
                      ...cardStyles.badge(p.badge),
                      ...(p.id === 'anthropic' && p.badge === 'Recommended' ? {
                        animation: 'pulse-badge 2s ease-in-out infinite',
                      } : {}),
                    }}
                  >
                    {p.badge}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Pulse animation for recommended badge */}
        <style>{`
          @keyframes pulse-badge {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
        `}</style>
      </div>

      {/* Selected Provider Config */}
      {provider && (
        <div style={{ ...cardStyles.card, marginTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={cardStyles.iconCircle(provider.color)}>
              {provider.icon}
            </div>
            <div>
              <h3 style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '700', margin: 0 }}>
                {provider.name} Configuration
              </h3>
              <p style={{ color: '#64748b', fontSize: '12px', margin: 0 }}>
                Model: {provider.model}
              </p>
            </div>
          </div>

          <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '20px', lineHeight: '1.5' }}>
            {provider.description}
          </p>

          {/* API Key Input */}
          <div style={{ marginBottom: '16px' }}>
            <label style={cardStyles.label}>API Key</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  if (connectionStatus !== STATUS.UNTESTED) {
                    setConnectionStatus(STATUS.UNTESTED);
                    setStatusMessage('');
                  }
                }}
                onFocus={() => setFocusedField('apiKey')}
                onBlur={() => setFocusedField(null)}
                style={focusedField === 'apiKey' ? cardStyles.inputFocused : cardStyles.input}
                placeholder={provider.apiKeyPrefix ? `${provider.apiKeyPrefix}...` : 'Enter your API key'}
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  fontSize: '13px',
                  padding: '4px',
                }}
              >
                {showKey ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Saved key display */}
          {profile?.llm_api_key_last4 && profile?.selected_llm_provider === selectedProvider && !apiKey && (
            <div style={{
              padding: '10px 14px',
              background: 'rgba(34, 197, 94, 0.08)',
              border: '1px solid rgba(34, 197, 94, 0.2)',
              borderRadius: '8px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span style={{ color: '#94a3b8', fontSize: '13px', fontFamily: 'monospace' }}>
                {getMaskedKey()}
              </span>
            </div>
          )}

          {/* Docs link */}
          <div style={{ marginBottom: '16px' }}>
            <a
              href={provider.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#f59e0b',
                fontSize: '13px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              Where to get your {provider.name} key
            </a>
          </div>

          {/* Connection Status */}
          {statusMessage && (
            <div style={{
              padding: '10px 14px',
              background: statusConfig[connectionStatus].bg,
              borderRadius: '8px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: statusConfig[connectionStatus].color,
                ...(connectionStatus === STATUS.TESTING ? {
                  animation: 'pulse-dot 1.5s ease-in-out infinite',
                } : {}),
              }} />
              <span style={{
                color: statusConfig[connectionStatus].color,
                fontSize: '13px',
                fontWeight: '500',
              }}>
                {statusMessage}
              </span>
            </div>
          )}

          <style>{`
            @keyframes pulse-dot {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.5; transform: scale(1.3); }
            }
          `}</style>

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={!apiKey.trim() || connectionStatus === STATUS.TESTING}
              style={{
                ...cardStyles.btnSecondary,
                opacity: !apiKey.trim() || connectionStatus === STATUS.TESTING ? 0.5 : 1,
                cursor: !apiKey.trim() || connectionStatus === STATUS.TESTING ? 'not-allowed' : 'pointer',
              }}
            >
              {connectionStatus === STATUS.TESTING ? 'Testing...' : 'Test Connection'}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{
                ...cardStyles.btnPrimary,
                opacity: saving ? 0.6 : 1,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LLMConfigTab;
